import React, { ReactNode, useState, useMemo } from 'react';
import {
    Paper,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TablePagination,
    TableCell,
    Box,
    styled,
    useTheme,
    Typography,
    IconButton,
    SelectChangeEvent
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Checkbox from '../../checkbox';
import CustomPagination from './CustomPagination';

// Định nghĩa cấu trúc của một cột
interface Column<T> {
    caption?: string; // Tiêu đề cột
    dataField?: keyof T; // Tên trường dữ liệu tương ứng trong object T
    renderHeader?: () => ReactNode; // Hàm tùy chỉnh để render tiêu đề cột
    renderCell?: (data: T, index?: number) => ReactNode; // Hàm tùy chỉnh để render nội dung ô
    width?: number | string; // Độ rộng cột
    minWidth?: number | string; // Độ rộng tối thiểu
    maxWidth?: number | string; // Độ rộng tối đa
    align?: 'left' | 'center' | 'right' | undefined; // Căn lề nội dung ô
}

// Định nghĩa các props cho component Datatable
interface DatatableProps<T> {
    size?: 'small' | 'normal' | 'large'; // Kích thước (ít ảnh hưởng đến style tùy chỉnh này)
    showPagination?: boolean; // Hiển thị phân trang?
    showCheckBox?: boolean; // Hiển thị checkbox?
    columns: Column<T>[]; // Mảng cấu hình các cột
    data: T[]; // Mảng dữ liệu
    noDataTitle?: string;
    minHeight?: string | number; // Chiều cao tối thiểu của bảng
    maxHeight?: string | number; // Chiều cao tối đa của bảng (cho phép scroll)
    groupByField?: keyof T | null; // Trường dữ liệu dùng để nhóm (hoặc null nếu không nhóm)
    defaultGroupsCollapsed?: boolean; // Các nhóm có mặc định đóng lại không?
    renderGroupTitle?: (groupKey: string, groupData: T[]) => ReactNode; // Hàm tùy chỉnh để render tiêu đề nhóm
}

// --- Styled Components ---

const StyledTable = styled(Table)(({ theme }) => ({
    borderCollapse: 'separate', // Quan trọng để border-radius hoạt động
    borderSpacing: '0', // Quan trọng để border-radius hoạt động
    width: '100%'
}));

// StyledTableRow chủ yếu xử lý border-left và hover/background
const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '& td, & th': {
        // Áp dụng cho cả td và th bên trong nếu có
        borderLeft: `1px solid ${theme.palette.divider}`,
        '&:first-of-type': {
            borderLeft: 0 // Bỏ border trái cho cell đầu tiên
        }
    },
    // Có thể thêm style hover ở đây nếu muốn
    '&:hover': {
        backgroundColor: theme.palette.action.hover // Ví dụ màu nền khi hover
    }
}));

// StyledTableCell sẽ quản lý border-bottom và padding
const StyledTableCell = styled(TableCell)(({ theme }) => ({
    padding: '4px 8px', // Giảm padding một chút
    // Luôn có border dưới cho mỗi cell để tạo đường kẻ hàng
    borderBottom: `1px solid ${theme.palette.divider}`
}));

// Style riêng cho Group Header Row
const GroupHeaderRow = styled(TableRow)(({ theme }) => ({
    backgroundColor: theme.palette.grey[100], // Màu nền hơi khác cho header group
    cursor: 'pointer',
    '& > td': {
        fontWeight: 'bold',
        // borderBottom đã có từ StyledTableCell
        // Thêm border trên để tách biệt với hàng trước đó
        borderTop: `1px solid ${theme.palette.divider}`,
        borderLeft: `1px solid ${theme.palette.divider}`, // Đảm bảo có border trái
        borderRight: `1px solid ${theme.palette.divider}`, // Thêm border phải cho cell header group
        '&:first-of-type': {
            borderLeft: 0 // Bỏ border trái cho cell đầu tiên của group header
        }
    },
    // Đảm bảo group header đầu tiên trong TableBody cũng có border-top
    '&:first-of-type > td': {
        borderTop: `1px solid ${theme.palette.divider}`
    },
    // Style hover riêng cho group header nếu muốn
    '&:hover': {
        backgroundColor: theme.palette.grey[200]
    }
}));

// --- Datatable Component ---

const Datatable = <T extends {}>({
    columns,
    data,
    showPagination = false,
    showCheckBox = false,
    minHeight = '60px',
    maxHeight = '600px', // Chiều cao tối đa mặc định
    noDataTitle = 'Không có dữ liệu',
    groupByField = null,
    defaultGroupsCollapsed = false,
    renderGroupTitle
}: DatatableProps<T>) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20); // Số dòng mỗi trang mặc định
    const theme = useTheme();

    // State quản lý trạng thái đóng/mở của các nhóm
    const [collapsedGroups, setCollapsedGroups] = useState<{
        [key: string]: boolean;
    }>(() => {
        if (!groupByField) return {}; // Không cần state nếu không group
        const initialCollapsedState: { [key: string]: boolean } = {};
        if (defaultGroupsCollapsed) {
            // Chỉ khởi tạo nếu mặc định là đóng
            data.forEach((item) => {
                const key = String(item[groupByField as keyof T]);
                if (!(key in initialCollapsedState)) {
                    initialCollapsedState[key] = true; // Mặc định đóng
                }
            });
        }
        // Nếu defaultGroupsCollapsed là false, trả về object rỗng (mặc định mở)
        return initialCollapsedState;
    });

    // Xử lý thay đổi trang
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    // Xử lý thay đổi số dòng mỗi trang
    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement> | SelectChangeEvent<number>) => {
        const value = event.target.value;
        const newRowsPerPage = parseInt(String(value), 10);
        if (!isNaN(newRowsPerPage)) {
            setRowsPerPage(newRowsPerPage);
            setPage(0);
        }
    };

    // Nhóm dữ liệu (chỉ tính toán lại khi data hoặc groupByField thay đổi)
    const groupedData = useMemo(() => {
        if (!groupByField) return null; // Trả về null nếu không nhóm
        const groups: { [key: string]: T[] } = {};
        data.forEach((item) => {
            const key = String(item[groupByField as keyof T]); // Đảm bảo key là string
            if (!groups[key]) {
                groups[key] = [];
            }
            groups[key].push(item);
        });
        return groups;
    }, [data, groupByField]);

    // Hàm thay đổi trạng thái đóng/mở của một nhóm
    const toggleGroup = (groupKey: string) => {
        setCollapsedGroups((prev) => ({
            ...prev,
            [groupKey]: !prev[groupKey] // Đảo ngược trạng thái hiện tại
        }));
    };

    // Tính toán số cột cần merge (colspan) cho header group và thông báo "không có dữ liệu"
    const colSpan = columns.length + (showCheckBox ? 1 : 0);

    // Xác định dữ liệu cần render (chỉ áp dụng phân trang khi không group)
    const dataToRender = useMemo(() => {
        if (!groupByField && showPagination) {
            // Phân trang nếu không group và showPagination là true
            return data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
        }
        // Trả về toàn bộ dữ liệu nếu có group hoặc không phân trang
        return data;
    }, [data, groupByField, showPagination, page, rowsPerPage]);

    return (
        <Paper
            sx={{
                width: '100%',
                overflow: 'hidden',
                borderRadius: '8px',
                border: `1px solid ${theme.palette.divider}`
            }}>
            <TableContainer
                style={{
                    // Không cần border ở đây vì Paper đã có
                    minHeight: minHeight,
                    maxHeight: showPagination ? maxHeight : `calc(${maxHeight} + 52px)` // Áp dụng chiều cao tối đa để scroll
                    // borderRadius: '8px', // Paper đã có
                }}>
                <StyledTable stickyHeader={true} aria-label="sticky table">
                    {/* --- Table Head --- */}
                    <TableHead
                        sx={{
                            position: 'sticky', // Đảm bảo sticky hoạt động đúng
                            top: 0, // Dính vào top
                            zIndex: theme.zIndex.appBar - 1, // Đảm bảo nằm trên body nhưng dưới Appbar nếu có
                            backgroundColor: '#f5f8fa', // Màu nền header
                            color: '#656D76', // Màu chữ
                            fontWeight: 600, // Độ đậm
                            fontSize: 13 // Cỡ chữ
                        }}>
                        {/* Sử dụng TableRow thường, style áp dụng qua sx và StyledTableCell */}
                        <TableRow
                            sx={{
                                // Style cho các ô tiêu đề (th)
                                '& th': {
                                    position: 'sticky', // Đảm bảo sticky hoạt động đúng
                                    top: 0, // Dính vào top
                                    zIndex: theme.zIndex.appBar - 1, // Đảm bảo nằm trên body nhưng dưới Appbar nếu có
                                    backgroundColor: '#f5f8fa', // Màu nền header
                                    color: '#656D76', // Màu chữ
                                    fontWeight: 600, // Độ đậm
                                    fontSize: 13, // Cỡ chữ
                                    // Đảm bảo border dưới cho header cells
                                    borderBottom: `1px solid ${theme.palette.divider} !important`,
                                    borderLeft: `1px solid ${theme.palette.divider}`
                                },
                                // Style riêng cho ô header đầu tiên
                                '& th:first-of-type': {
                                    borderTopLeftRadius: '4px', // Bo góc trên trái
                                    borderLeft: 0 // Bỏ border trái
                                },
                                // Style riêng cho ô header cuối cùng
                                '& th:last-of-type': {
                                    borderTopRightRadius: '4px', // Bo góc trên phải
                                    // Đảm bảo border phải
                                    borderRight: `1px solid ${theme.palette.divider}`
                                }
                            }}>
                            {/* Checkbox chọn tất cả (nếu bật) */}
                            {showCheckBox && (
                                <StyledTableCell
                                    key={'checkboxAll'}
                                    align="center"
                                    sx={{
                                        width: 40, // Độ rộng cố định cho checkbox
                                        // Các style chung đã được định nghĩa ở TableRow sx
                                        borderBottom: `1px solid ${theme.palette.divider} !important` // Ghi đè nếu cần
                                    }}>
                                    {/* Checkbox bị vô hiệu hóa khi đang group */}
                                    <Checkbox size="small" disabled={!!groupByField} />
                                </StyledTableCell>
                            )}
                            {/* Render các tiêu đề cột */}
                            {columns.map((column, index) => (
                                <StyledTableCell // Dùng StyledTableCell để nhất quán padding
                                    key={String(column.dataField) || `header-${index}`} // Key duy nhất
                                    style={{
                                        // Style inline cho các thuộc tính động từ column
                                        minWidth: column.minWidth,
                                        width: column.width,
                                        maxWidth: column.maxWidth,
                                        textAlign: column.align
                                    }}
                                    sx={{
                                        // Các style chung đã được định nghĩa ở TableRow sx
                                        borderBottom: `1px solid ${theme.palette.divider} !important` // Ghi đè nếu cần
                                    }}>
                                    {/* Ưu tiên renderHeader nếu có, ngược lại dùng caption */}
                                    {column.renderHeader ? column.renderHeader() : column.caption}
                                </StyledTableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    {/* --- Table Body --- */}
                    <TableBody
                        sx={{
                            // Loại bỏ border dưới của hàng cuối cùng trong body
                            '& > tr:last-child > td': {
                                borderBottom: 'none'
                            },
                            // Đảm bảo border phải cho cell cuối cùng của mỗi hàng
                            '& > tr > td:last-child': {
                                borderRight: `1px solid ${theme.palette.divider}`
                            }
                        }}>
                        {data && data.length > 0 ? (
                            // --- Render dữ liệu theo nhóm ---
                            groupByField && groupedData ? (
                                Object.entries(groupedData).map(([groupKey, groupData]) => (
                                    <React.Fragment key={groupKey}>
                                        {/* --- Hàng Tiêu đề Nhóm --- */}
                                        <GroupHeaderRow onClick={() => toggleGroup(groupKey)}>
                                            {/* Cell này chiếm toàn bộ hàng */}
                                            <StyledTableCell colSpan={colSpan}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5
                                                    }}>
                                                    {/* Icon đóng/mở */}
                                                    <IconButton size="small" sx={{ padding: '2px' }}>
                                                        {collapsedGroups[groupKey] ? (
                                                            <KeyboardArrowDownIcon fontSize="small" />
                                                        ) : (
                                                            <KeyboardArrowUpIcon fontSize="small" />
                                                        )}
                                                    </IconButton>

                                                    {/* Nội dung tiêu đề nhóm (tùy chỉnh hoặc mặc định) */}
                                                    {renderGroupTitle ? (
                                                        renderGroupTitle(groupKey, groupData) // Gọi hàm tùy chỉnh
                                                    ) : (
                                                        <Typography variant="subtitle2" component="span">
                                                            {/* Hiển thị mặc định */}
                                                            {groupKey}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </StyledTableCell>
                                        </GroupHeaderRow>

                                        {/* --- Hàng Dữ liệu trong Nhóm (chỉ render nếu nhóm mở) --- */}
                                        {!collapsedGroups[groupKey] &&
                                            groupData.map((row: T, index: number) => (
                                                // Sử dụng StyledTableRow cho các hàng data
                                                <StyledTableRow
                                                    role="checkbox" // Giữ role nếu có checkbox
                                                    tabIndex={-1}
                                                    key={`row-${groupKey}-${index}`} // Key duy nhất, ưu tiên row.id nếu có
                                                    sx={{
                                                        backgroundColor:
                                                            index % 2
                                                                ? theme.palette.action.hover
                                                                : theme.palette.background.paper
                                                    }} // Ví dụ: màu nền xen kẽ
                                                >
                                                    {/* Checkbox cho từng dòng (nếu bật) */}
                                                    {showCheckBox && (
                                                        <StyledTableCell align="center" key={`cb-${groupKey}-${index}`}>
                                                            <Checkbox size="small" />
                                                        </StyledTableCell>
                                                    )}
                                                    {/* Render các ô dữ liệu */}
                                                    {columns.map((column, colIndex) => (
                                                        <StyledTableCell
                                                            key={
                                                                String(column.dataField) ||
                                                                `cell-${groupKey}-${index}-${colIndex}`
                                                            }
                                                            align={column.align}>
                                                            {/* Ưu tiên renderCell nếu có, ngược lại dùng dataField */}
                                                            {column.renderCell
                                                                ? column.renderCell(row, index)
                                                                : column.dataField
                                                                ? String(row[column.dataField] ?? '')
                                                                : ''}{' '}
                                                            {/* Xử lý giá trị null/undefined */}
                                                        </StyledTableCell>
                                                    ))}
                                                </StyledTableRow>
                                            ))}
                                    </React.Fragment>
                                ))
                            ) : (
                                // --- Render dữ liệu phẳng (không nhóm) ---
                                dataToRender.map((row, index) => (
                                    <StyledTableRow
                                        role="checkbox"
                                        tabIndex={-1}
                                        key={`row-${index}`} // Key duy nhất
                                        sx={{
                                            backgroundColor:
                                                index % 2 ? theme.palette.action.hover : theme.palette.background.paper
                                        }} // Ví dụ: màu nền xen kẽ
                                    >
                                        {/* Checkbox cho từng dòng (nếu bật) */}
                                        {showCheckBox && (
                                            <StyledTableCell align="center" key={`cb-${index}`}>
                                                <Checkbox size="small" />
                                            </StyledTableCell>
                                        )}
                                        {/* Render các ô dữ liệu */}
                                        {columns.map((column, colIndex) => (
                                            <StyledTableCell
                                                key={String(column.dataField) || `cell-${index}-${colIndex}`}
                                                align={column.align}>
                                                {column.renderCell
                                                    ? column.renderCell(row, index)
                                                    : column.dataField
                                                    ? String(row[column.dataField] ?? '')
                                                    : ''}{' '}
                                                {/* Xử lý giá trị null/undefined */}
                                            </StyledTableCell>
                                        ))}
                                    </StyledTableRow>
                                ))
                            )
                        ) : (
                            // --- Hàng Thông báo "Không có dữ liệu" ---
                            <TableRow>
                                <StyledTableCell
                                    colSpan={colSpan}
                                    align="center"
                                    // Đảm bảo cell này cũng có border phải và không có border dưới
                                    sx={{
                                        borderRight: `1px solid ${theme.palette.divider}`,
                                        borderBottom: 'none'
                                    }}>
                                    <Box py={2}>
                                        <Typography variant="body2" color="textSecondary">
                                            {noDataTitle}
                                        </Typography>
                                    </Box>
                                </StyledTableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </StyledTable>
            </TableContainer>

            {/* --- Phân trang (chỉ hiển thị khi bật và không group) --- */}
            {/* {showPagination && !groupByField && data.length > 0 && (
                <TablePagination
                    sx={{
                        borderTop: `1px solid ${theme.palette.divider}`, // Đường kẻ phía trên phân trang
                        height: '52px', // Chiều cao phân trang
                        overflow: 'hidden'
                    }}
                    rowsPerPageOptions={[10, 20, 50, 100]} // Các tùy chọn số dòng
                    component="div"
                    count={data.length} // Tổng số dòng (dùng cho tính toán phân trang)
                    rowsPerPage={rowsPerPage} // Số dòng đang hiển thị mỗi trang
                    page={page} // Trang hiện tại (bắt đầu từ 0)
                    onPageChange={handleChangePage} // Hàm xử lý khi chuyển trang
                    onRowsPerPageChange={handleChangeRowsPerPage} // Hàm xử lý khi thay đổi số dòng mỗi trang
                    labelRowsPerPage="Số dòng mỗi trang:" // Nhãn tùy chỉnh
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} của ${count !== -1 ? count : `hơn ${to}`}`
                    } // Nhãn hiển thị số dòng
                />
            )} */}
            {showPagination && !groupByField && data.length > 0 && (
                <CustomPagination
                    data={data}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                />
            )}
        </Paper>
    );
};

export default Datatable;
